import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import { ID } from '../../../../../../../types';
import {
  UniversalFilterTagsValue,
  UniversalFilterTagsModes,
  UniversalFilterTagsFilterState,
  UniversalFilterTagsOnFilterStateChange,
  UniversalFilterTagsOnChange
} from '../../UniversalFilterTags.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterTagsFilterState } from '../useUniversalFilterTagsFilterState';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterTagsModes.CONTAINS_ANY_OF;

const modeOptions: UniversalFilterTemplateModeOption<UniversalFilterTagsModes>[] =
  [
    {
      value: UniversalFilterTagsModes.CONTAINS_ANY_OF,
      i18nText: filtersKeys.modes.containsAnyOf
    }
  ];

interface UniversalFilterTagsOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterTagsValue;
  onChange: UniversalFilterTagsOnChange;
}

function useUniversalFilterTags({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterTagsOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterTagsFilterState>(filterStateCacheKey);

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterTagsFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterTagsFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: defaultMode,
          tagIds: []
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterTagsOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterTagsModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        tagIds: filterState.tagIds || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  const handleTagIdsChange = useCallback<(tagIds: ID[]) => void>(
    (tagIds) => {
      handleFilterStateChange({
        mode: filterState.mode,
        tagIds: tagIds || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleTagIdsChange
  };
}

export default useUniversalFilterTags;
